import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getLatestPsychologistReviewAPI,
  getPsychologistInspectionAPI,
  getPsychologistReviewAPI,
  psychologistReviewAPI,
  updatePsychologistReviewAPI,
} from "../../../../services/psychologist.services";
import {
  Button,
  Col,
  Drawer,
  Form,
  Image,
  message,
  Modal,
  Row,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import PsychologistDiagnosis from "../../InspectionForms/Symptoms/PsychologistDiagnosis";
import { PsychologistReviewContext } from "./PsychologistReviewProvider";
import PsychologistTherapiesSuggested from "../../InspectionForms/Therapy/PsychologistTherapiesSuggested";
import PsychologistScales from "../../InspectionForms/Scales/PsychologistScales";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";
import "../../Doctor/DailyReview/DailyReview.css";

const PsychologistReviewForm = ({
  editId,
  visible,
  setVisible,
  refreshPage,
  iAssessment,
  addMode,
  setAddMode,
  setActiveSegment,
  reviewData,
  setDailyReviewRefresh,
  dailyRefresh,
  segmentData,
  patientData,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [diagnosisForm] = Form.useForm();
  const [scalesForm] = Form.useForm();
  const [therapyForm] = Form.useForm();
  const { formData, setFormData } = useContext(PsychologistReviewContext);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const newReview = reviewData?.length === 0;
  const reviewId = reviewData?.[0]?.id;
  const newReviewId = addMode === false && reviewId ? reviewId : null;
  const reviewDate =
    segmentData?.find(
      (option) => option.value === editId && option.value !== "others"
    )?.label ||
    new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Diagnosis" : "Psychology Diagnosis",
      },
      {
        key: "2",
        label: isMobile ? "Therapies" : "Therapies Suggested",
      },
      { key: "3", label: "Scales" },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId || newReviewId) {
        await updatePsychologistReviewAPI(editId || newReviewId, values);
        setDailyReviewRefresh();
      } else {
        await psychologistReviewAPI(values);
        refreshPage();
        setAddMode(false);
        setDailyReviewRefresh();
      }

      message.success(`${flag} submitted successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onDiagnosisSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        psychology_diagnosis: values,
        admission_id: params?.patient,
      },
      "Psychology Diagnosis"
    );
  };
  const onScalesSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        scales: values,
        admission_id: params?.patient,
      },
      "Scales"
    );
  };
  const onTherapySubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        therapies_suggested: values,
        admission_id: params?.patient,
      },
      "Therapies Suggested"
    );
  };

  const openEdit = async () => {
    if (editId && newReview === false) {
      try {
        const response = await getPsychologistReviewAPI(editId);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else if (newReview === false) {
      try {
        const response = await getLatestPsychologistReviewAPI(params?.patient);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else {
      try {
        const response = await getPsychologistInspectionAPI(iAssessment);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error:${error}`);
      }
    }
  };

  useEffect(() => {
    openEdit();
  }, [editId, dailyRefresh]);

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return diagnosisForm.submit();
      case "2":
        return therapyForm.submit();
      case "3":
        return scalesForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      switch (activeTab) {
        case "1":
          diagnosisForm.validateFields().then((values) => {
            setIsDirty(false);
            onDiagnosisSubmit(values);
            setActiveTab(key);
          });
          break;
        case "2":
          therapyForm.validateFields().then((values) => {
            setIsDirty(false);
            onTherapySubmit(values);
            setActiveTab(key);
          });
          break;
        case "3":
          scalesForm.validateFields().then((values) => {
            setIsDirty(false);
            onScalesSubmit(values);
            setActiveTab(key);
          });
          break;
        default:
          setActiveTab(key);
      }
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  const handleCloseConfirm = () => {
    Modal.confirm({
      title: "Confirmation",
      content: "Are you sure you want to close?",
      onOk: () => {
        setVisible(false);
        setActiveSegment(reviewData[0]?.id);
      },
    });
  };

  const patientName = `${patientData?.admission?.patient?.first_name || ""} ${
    patientData?.admission?.patient?.last_name || ""
  }`.trim();

  return (
    <div>
      <Drawer
        styles={{
          body: {
            overflow: "hidden",
            paddingTop: 0,
            paddingBottom: 0,
            paddingInline: 24,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
        open={visible}
        width="100%"
        title={
          <div className="ia-drawer-header">
            <div className="ia-drawer-header__main">
              <LeftOutlined
                className="ia-drawer-header__back"
                onClick={handleCloseConfirm}
              />
              <div className="ia-drawer-header__identity">
                <div className="ia-drawer-header__name-row">
                  <Typography.Text
                    className="ia-drawer-header__name"
                    ellipsis={{ tooltip: patientName }}
                  >
                    {patientName}
                  </Typography.Text>
                  {patientData?.admission?.bed?.bed_no && (
                    <Tag color="orange" className="ia-drawer-header__bed">
                      <Image src={bedImg} preview={false} width={12} />{" "}
                      {patientData?.admission?.bed?.bed_no}
                    </Tag>
                  )}
                </div>
                <div className="ia-drawer-header__meta">
                  <Tag className="ia-drawer-header__tag">
                    {patientData?.admission?.patient?.gender}{" "}
                    {patientData?.admission?.patient?.age}
                  </Tag>
                  <Tag className="ia-drawer-header__tag">
                    IP ID: {patientData?.admission?.ip_id}
                  </Tag>
                  {reviewDate && (
                    <Tag className="ia-drawer-header__tag">{reviewDate}</Tag>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={null}
        onClose={() => {
          setVisible(false);
          setActiveSegment(reviewData[0]?.id);
        }}
        closeIcon={null}
        keyboard={false}
      >
        <div className="daily-review-form-shell">
          <div className="ia-form-tabs daily-review-form-tabs">
            <Row>
              <Col
                xs={24}
                sm={24}
                md={{ span: 23, offset: 1 }}
                lg={{ span: 21, offset: 3 }}
                xl={{ span: 19, offset: 5 }}
                xxl={{ span: 18, offset: 6 }}
              >
                <Tabs
                  activeKey={activeTab}
                  items={tabItems}
                  onChange={handleTabChange}
                />
              </Col>
            </Row>
          </div>

          <div className="ia-form-panel">
            <div className="ia-form-body" ref={formBodyRef}>
              {activeTab === "1" && (
                <Form
                  id="review-psychology-diagnosis"
                  form={diagnosisForm}
                  layout="vertical"
                  onFinish={onDiagnosisSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <PsychologistDiagnosis
                    form={diagnosisForm}
                    context={PsychologistReviewContext}
                  />
                </Form>
              )}

              {activeTab === "2" && (
                <Form
                  id="review-therapies-suggested"
                  form={therapyForm}
                  layout="vertical"
                  onFinish={onTherapySubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <PsychologistTherapiesSuggested
                    form={therapyForm}
                    context={PsychologistReviewContext}
                  />
                </Form>
              )}

              {activeTab === "3" && (
                <Form
                  id="review-scales-form"
                  form={scalesForm}
                  layout="vertical"
                  onFinish={onScalesSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <PsychologistScales form={scalesForm} />
                </Form>
              )}
            </div>

            <div className="ia-form-footer">
              {activeTab !== "3" && (
                <Button
                  onClick={() => {
                    getFormKey();
                    setVisible(false);
                  }}
                  style={{ marginRight: 8 }}
                >
                  Save & Close
                </Button>
              )}
              <Button
                onClick={() => {
                  getFormKey();
                  document.querySelector(".ip-forms")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                  const nextTab = (parseInt(activeTab) + 1).toString();
                  if (activeTab === "3") {
                    setVisible(false);
                  } else {
                    setActiveTab(nextTab);
                  }
                }}
                type="primary"
              >
                {activeTab === "3" ? "Done" : " Save & Proceed"}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default PsychologistReviewForm;
